import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import SvgMapper from "../features/common/SvgMapper";
import { getSettings, updateSettings } from "../../application/services/settingsService";


const mapProviderToSelect = (provider) => {
  if (!provider) return "Gemini";
  const p = String(provider).toUpperCase();
  return p === "OPENAI" ? "OpenAI" : p === "GEMINI" ? "Gemini" : provider;
};

const mapProviderToApi = (provider) => {
  if (!provider) return "GEMINI";
  const p = String(provider).toUpperCase();
  return p === "OPENAI" ? "OPENAI" : "GEMINI";
};

const SettingsTab = () => {
  const [apiProvider, setApiProvider] = useState("Gemini");
  const [apiKey, setApiKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [apiKeyPlaceholder, setApiKeyPlaceholder] = useState("Enter your API key");
  const [initialApiProvider, setInitialApiProvider] = useState(null);
  const [initialApiKeyValue, setInitialApiKeyValue] = useState("");
  const adminUser = useSelector((state) => state.login.admin?.user);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await getSettings();
        const loadedProvider = data?.api_key_provider ? mapProviderToSelect(data.api_key_provider) : "Gemini";

        setApiProvider(loadedProvider);
        setInitialApiProvider(loadedProvider);
        const keyFromApi = data?.api_key_value && typeof data.api_key_value === "string" ? data.api_key_value : "";
        setInitialApiKeyValue(keyFromApi);
        setApiKeyPlaceholder(keyFromApi.includes("*") ? keyFromApi : "Enter your API key");
      } catch (err) {
        console.error("Failed to fetch settings:", err);
        setInitialApiProvider("Gemini");
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const hasChanges = apiProvider !== initialApiProvider || (apiKey?.trim() ?? "") !== "";

  const handleSave = async () => {
    const apiKeyToSend = (apiKey?.trim() ?? "") !== "" ? apiKey.trim() : initialApiKeyValue;

    const payload = {
      api_key_provider: mapProviderToApi(apiProvider),
      api_key_value: apiKeyToSend,
      created_by: adminUser?.email || "",
    };

    setSaving(true);
    try {
      const response = await updateSettings(payload);
      setInitialApiProvider(apiProvider);
      if (response?.api_key_value) {
        setInitialApiKeyValue(response.api_key_value);
        setApiKeyPlaceholder(response.api_key_value);
      }
      setApiKey("");
    } catch (err) {
      const msg = err?.message || err?.error || "Failed to save settings";
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[200px]">
        <p className="text-gray-600">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">

      <div className="bg-white rounded-lg border border-gray-200 p-6 mt-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">API Configuration</h2>
          <p className="text-sm text-gray-600 mt-1">
            Configure your AI provider API key
          </p>
        </div>

        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <label className="w-32 text-sm font-medium text-gray-700">
              Provider:
            </label>
            <select
              value={apiProvider}
              onChange={(e) => setApiProvider(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="Gemini">Gemini</option>
              <option value="OpenAI">OpenAI</option>
            </select>
          </div>

          <div className="flex items-center space-x-4">
            <label className="w-32 text-sm font-medium text-gray-700">
              API KEY:
            </label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={apiKeyPlaceholder}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-end space-x-3">
        <button
          onClick={handleSave}
          disabled={ saving || !hasChanges}
          className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
             saving || !hasChanges
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700"
          }`}
        >
          {SvgMapper.saveIcon("w-4 h-4")}
          <span>{saving ? "Saving..." : "Save All Settings"}</span>
        </button>
      </div>
    </div>
  );
};

export default SettingsTab;
